import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  GoogleMap,
  MarkerF,
  MarkerClustererF,
  InfoWindowF,
} from "@react-google-maps/api";
import { useGoogleMaps } from "../contexts/GoogleMapsContext";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItem,
  IconButton,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  ListItemAvatar,
  TextField,
  Paper,
  ListItemText,
  ListItemButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LockIcon from "@mui/icons-material/Lock";
import { apiCall } from "../config/api";

const MAP_API_KEY =
  process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY_HERE";

// Yükseklik hesaplaması: AppBar (64px), sayfa içi paddingler (32px), arama çubuğu (104px) = ~200px
const containerStyleConfig = {
  width: "100%",
  height: "calc(100vh - 200px)",
};

const defaultCenter = {
  lat: 39.925533,
  lng: 32.866287, // Ankara merkezi
};

function MapManagementPage() {
  const [pins, setPins] = useState([]);
  const [allPins, setAllPins] = useState([]);
  const [selectedClusterPins, setSelectedClusterPins] = useState([]);
  const [clusterModalOpen, setClusterModalOpen] = useState(false);
  const mapRef = useRef(null);
  const [dataLoading, setDataLoading] = useState(true); // For Firestore data
  const [searchTerm, setSearchTerm] = useState("");
  const [dbError, setDbError] = useState(null);
  const [selectedPinForInfo, setSelectedPinForInfo] = useState(null);

  const { isLoaded, loadError } = useGoogleMaps();

  const onLoadMap = useCallback(function callback(map) {
    mapRef.current = map;
  }, []);

  const onUnmountMap = useCallback(function callback(map) {
    mapRef.current = null;
  }, []);

  const handleMarkerClick = (pin) => {
    setSelectedPinForInfo(pin);
    if (clusterModalOpen) {
      setClusterModalOpen(false);
    }
  };

  const fetchPins = useCallback(async () => {
    setDataLoading(true);
    setDbError(null);
    try {
      const response = await apiCall("/api/pins", "GET");
      if (!response.success) {
        throw new Error(response.error || "Failed to fetch pins");
      }

      console.log("API Response:", response);
      console.log("Raw pins data:", response.pins);

      const pinsData = (response.pins || [])
        .filter((pin) => pin.isDeleted !== true)
        .map((docData) => {
          console.log("Processing pin:", docData);
          let parsedLat = NaN,
            parsedLng = NaN;

          // First check direct latitude/longitude fields (most common from API)
          if (
            docData.latitude !== undefined &&
            docData.longitude !== undefined
          ) {
            parsedLat = parseFloat(docData.latitude);
            parsedLng = parseFloat(docData.longitude);
          } else if (docData.location) {
            if (
              docData.location._lat !== undefined &&
              docData.location._long !== undefined
            ) {
              parsedLat = parseFloat(docData.location._lat);
              parsedLng = parseFloat(docData.location._long);
            } else if (
              docData.location.latitude !== undefined &&
              docData.location.longitude !== undefined
            ) {
              parsedLat = parseFloat(docData.location.latitude);
              parsedLng = parseFloat(docData.location.longitude);
            }
          }

          console.log(`Pin ${docData.id} coordinates:`, {
            parsedLat,
            parsedLng,
            isValid: !isNaN(parsedLat) && !isNaN(parsedLng),
          });

          return {
            id: docData.id,
            title: docData.title || docData.name || "Başlıksız",
            description: docData.description || "",
            audioUrl: docData.audioUrl || "",
            isProtected: docData.isProtected || false,
            pinType: docData.pinType || "text",
            pinTier: docData.pinTier || "Free",
            status: docData.status || "Aktif",
            createdAt: docData.createdAt,
            userId: docData.userId || "",
            userData: {
              // PRIORITY: senderName (mobile app) → pinOwnerName (backend enriched) → fallback
              displayName:
                docData.senderName ||
                docData.pinOwnerName ||
                "Bilinmeyen Kullanıcı",
              // PRIORITY: userProfileImageUrl (mobile app) → pinOwnerPhotoUrl (backend enriched) → fallback
              photoURL:
                docData.userProfileImageUrl || docData.pinOwnerPhotoUrl || null,
              email: "N/A",
            },
            campaignName: docData.campaignName || null,
            campaignLogoUrl: docData.campaignLogoUrl || null,
            lat: isNaN(parsedLat) ? 0 : parsedLat,
            lng: isNaN(parsedLng) ? 0 : parsedLng,
            isValidLocation: !isNaN(parsedLat) && !isNaN(parsedLng),
          };
        });

      setAllPins(pinsData);
      setPins(pinsData);
      setDataLoading(false);
    } catch (error) {
      console.error("Veri çekme hatası:", error);
      setDbError("Veri yüklenirken bir hata oluştu.");
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPins();
    // Polling kaldırıldı - manuel yenileme için
    // const interval = setInterval(fetchPins, 30000);
    // return () => clearInterval(interval);
  }, [fetchPins]);

  useEffect(() => {
    if (!searchTerm) {
      setPins(allPins);
      return;
    }
    const lowercasedFilter = searchTerm.toLowerCase();
    const filteredData = allPins.filter((pin) => {
      const titleMatch =
        typeof pin.title === "string" &&
        pin.title.toLowerCase().includes(lowercasedFilter);
      const descriptionMatch =
        typeof pin.description === "string" &&
        pin.description.toLowerCase().includes(lowercasedFilter);
      return titleMatch || descriptionMatch;
    });
    setPins(filteredData);
  }, [searchTerm, allPins]);

  // Seçili küme listesinde artık var olmayan pinleri ayıkla
  useEffect(() => {
    if (selectedClusterPins.length === 0) return;
    const validIds = new Set(allPins.map((p) => p.id));
    setSelectedClusterPins((prev) => prev.filter((p) => validIds.has(p.id)));
  }, [allPins, selectedClusterPins.length]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleClusterClick = async (clusterer) => {
    const markerInstances = clusterer.getMarkers();
    const clickedPinData = markerInstances.map((marker) => marker.pinData);

    const pinsWithFullUserData = await Promise.all(
      clickedPinData.map(async (pin) => {
        return pin;
      })
    );

    setSelectedClusterPins(pinsWithFullUserData.filter((p) => p)); // Filter out any undefined pins
    setClusterModalOpen(true);
    setSelectedPinForInfo(null);
  };

  if (!MAP_API_KEY || MAP_API_KEY === "YOUR_GOOGLE_MAPS_API_KEY_HERE") {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: containerStyleConfig.height }}
      >
        <Typography variant="h6" color="error">
          Google Maps API Anahtarı yapılandırılmamış.
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: containerStyleConfig.height }}
      >
        <Typography color="error">
          Harita yüklenemedi: {loadError.message}
        </Typography>
      </Box>
    );
  }

  if (!isLoaded || dataLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: containerStyleConfig.height }}
      >
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>
          {!isLoaded
            ? "Harita altyapısı hazırlanıyor..."
            : "Pinler yükleniyor..."}
        </Typography>
      </Box>
    );
  }

  if (dbError) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        sx={{ height: containerStyleConfig.height }}
      >
        <Typography color="error">{dbError}</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        p: 2,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Sayfa başlığı kaldırıldı */}
      {/* 
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center', mb: 2 }}>
        Harita Yönetimi
      </Typography> 
      */}
      <Paper elevation={3} sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth
          label="Pin Başlığı veya Açıklamasında Ara"
          variant="outlined"
          value={searchTerm}
          onChange={handleSearchChange}
        />
      </Paper>

      <Box
        sx={{
          height: containerStyleConfig.height,
          width: "100%",
          position: "relative",
        }}
      >
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={defaultCenter}
          zoom={6}
          onLoad={onLoadMap}
          onUnmount={onUnmountMap}
          options={{
            gestureHandling: "cooperative",
            disableDefaultUI: false,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
          }}
        >
          <MarkerClustererF
            options={{
              gridSize: 60,
              maxZoom: 15,
              minimumClusterSize: 2,
              styles: [
                {
                  textColor: "white",
                  textSize: 12,
                  fontWeight: "bold",
                  fontFamily: "Arial, sans-serif",
                  url:
                    "data:image/svg+xml;base64," +
                    btoa(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="37" height="37" viewBox="0 0 37 37">
                      <circle cx="18.5" cy="18.5" r="18.5" fill="#1976d2" stroke="#fff" stroke-width="2"/>
                    </svg>
                  `),
                  height: 37,
                  width: 37,
                },
                {
                  textColor: "white",
                  textSize: 13,
                  fontWeight: "bold",
                  fontFamily: "Arial, sans-serif",
                  url:
                    "data:image/svg+xml;base64," +
                    btoa(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 39 39">
                      <circle cx="19.5" cy="19.5" r="19.5" fill="#1976d2" stroke="#fff" stroke-width="2"/>
                    </svg>
                  `),
                  height: 39,
                  width: 39,
                },
                {
                  textColor: "white",
                  textSize: 14,
                  fontWeight: "bold",
                  fontFamily: "Arial, sans-serif",
                  url:
                    "data:image/svg+xml;base64," +
                    btoa(`
                    <svg xmlns="http://www.w3.org/2000/svg" width="46" height="46" viewBox="0 0 46 46">
                      <circle cx="23" cy="23" r="23" fill="#1976d2" stroke="#fff" stroke-width="2"/>
                    </svg>
                  `),
                  height: 46,
                  width: 46,
                },
              ],
            }}
            onClick={handleClusterClick}
          >
            {(clusterer) =>
              pins
                .filter((pin) => pin.isValidLocation)
                .map((pin) => (
                  <MarkerF
                    key={pin.id}
                    position={{ lat: pin.lat, lng: pin.lng }}
                    clusterer={clusterer}
                    onClick={() => handleMarkerClick(pin)}
                    onLoad={(marker) => {
                      marker.pinData = pin;
                    }}
                    icon={
                      pin.campaignLogoUrl
                        ? {
                            url: pin.campaignLogoUrl,
                            scaledSize: new window.google.maps.Size(36, 36),
                            anchor: new window.google.maps.Point(18, 18),
                          }
                        : pin.userData?.photoURL
                        ? {
                            url: pin.userData.photoURL,
                            scaledSize: new window.google.maps.Size(36, 36),
                            anchor: new window.google.maps.Point(18, 18),
                          }
                        : pin.pinType === "audio"
                        ? {
                            url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                          }
                        : undefined // Varsayılan Google Maps marker
                    }
                  />
                ))
            }
          </MarkerClustererF>

          {selectedPinForInfo && selectedPinForInfo.isValidLocation && (
            <InfoWindowF
              position={{
                lat: selectedPinForInfo.lat,
                lng: selectedPinForInfo.lng,
              }}
              onCloseClick={() => setSelectedPinForInfo(null)}
              options={{ pixelOffset: new window.google.maps.Size(0, -35) }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  p: 1,
                  minWidth: "250px",
                  maxWidth: "350px",
                }}
              >
                {selectedPinForInfo.userData && (
                  <Avatar
                    alt={selectedPinForInfo.userData.displayName || "Kullanıcı"}
                    src={selectedPinForInfo.userData.photoURL || undefined}
                    sx={{ mr: 1.5, width: 40, height: 40, mt: 0.5 }}
                  >
                    {!selectedPinForInfo.userData.photoURL &&
                    selectedPinForInfo.userData.displayName
                      ? selectedPinForInfo.userData.displayName
                          .charAt(0)
                          .toUpperCase()
                      : null}
                  </Avatar>
                )}
                <Box sx={{ flexGrow: 1 }}>
                  <Typography
                    variant="subtitle1"
                    component="div"
                    sx={{
                      fontWeight: "bold",
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                      mb: 0.25,
                    }}
                  >
                    {selectedPinForInfo.title || "Pin Detayı"}
                  </Typography>
                  {selectedPinForInfo.userData &&
                    selectedPinForInfo.userData.displayName && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: "text.secondary",
                          mb: 0.25,
                          wordBreak: "break-word",
                        }}
                      >
                        Ekleyen: {selectedPinForInfo.userData.displayName}
                      </Typography>
                    )}
                  {selectedPinForInfo.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        mb: 0.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                      }}
                    >
                      {selectedPinForInfo.description}
                    </Typography>
                  )}
                  {selectedPinForInfo.id && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ color: "text.secondary", wordBreak: "break-all" }}
                    >
                      ID: {selectedPinForInfo.id}
                    </Typography>
                  )}
                </Box>
              </Box>
            </InfoWindowF>
          )}
        </GoogleMap>
      </Box>

      <Dialog
        open={clusterModalOpen}
        onClose={() => setClusterModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Kümedeki Pinler
          <IconButton
            aria-label="close"
            onClick={() => setClusterModalOpen(false)}
            sx={{ position: "absolute", right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <List>
            {selectedClusterPins.map((pin) => (
              <ListItem
                key={pin.id}
                disablePadding
                sx={{ mb: 1, border: "1px solid #eee", borderRadius: "4px" }}
              >
                <ListItemButton>
                  <ListItemAvatar>
                    <Avatar
                      src={pin.userData?.photoURL || undefined}
                      alt={pin.userData?.displayName || "K"}
                    >
                      {!pin.userData?.photoURL && pin.userData?.displayName
                        ? pin.userData.displayName.charAt(0).toUpperCase()
                        : "P"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1">
                        {pin.title || "Başlıksız Pin"}
                        {pin.isProtected && (
                          <LockIcon
                            fontSize="inherit"
                            sx={{ ml: 0.5, verticalAlign: "middle" }}
                          />
                        )}
                      </Typography>
                    }
                    secondary={
                      <>
                        <Typography
                          component="span"
                          variant="body2"
                          color="text.primary"
                        >
                          Ekleyen:{" "}
                          {pin.userData?.displayName ||
                            pin.userData?.email ||
                            "Bilinmiyor"}
                        </Typography>
                        <br />
                        {pin.description && (
                          <>
                            <Typography component="span" variant="caption">
                              {pin.description.substring(0, 100)}
                              {pin.description.length > 100 ? "..." : ""}
                            </Typography>
                            <br />
                          </>
                        )}
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ fontStyle: "italic" }}
                        >
                          ID: {pin.id}
                        </Typography>
                        {pin.pinType === "audio" &&
                          pin.audioURL &&
                          !pin.isProtected && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                new Audio(pin.audioURL)
                                  .play()
                                  .catch((err) =>
                                    console.error("Error playing audio:", err)
                                  );
                              }}
                            >
                              <PlayArrowIcon />
                            </IconButton>
                          )}
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default MapManagementPage;
